/**
 * MGRS Grid Canvas Overlay
 *
 * Custom Leaflet layer that renders MGRS grid lines, edge labels, and
 * Grid Zone Designator (GZD) labels on a canvas overlay. Also exports
 * a lightweight React wrapper for use inside a react-leaflet MapContainer.
 *
 * Grid line density scales with zoom:
 *   zoom  6-8  => 100km grid (major lines)
 *   zoom  9-11 => 10km grid
 *   zoom 12-14 => 1km grid
 *   zoom 15+   => 100m grid
 */

import L from 'leaflet';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { computeGridLines, getGridInterval } from '../utils/gridCalc.js';
import { latLngToMgrs } from '../utils/mgrsConvert.js';

// ─── Style constants ──────────────────────────────────────────────────────

/** Grid line color — semi-transparent amber/gold */
export const GRID_LINE_COLOR = 'rgba(200, 164, 21, 0.4)';

/** Minor grid line width in pixels */
export const GRID_LINE_WIDTH = 1;

/** Major (100km boundary) grid line width in pixels */
export const GRID_LINE_WIDTH_MAJOR = 1.5;

/** Label font for edge grid labels */
export const LABEL_FONT = '11px monospace';

/** Label text color — amber/gold, slightly more opaque than lines */
export const LABEL_COLOR = 'rgba(200, 164, 21, 0.7)';

/** Background rectangle color behind labels */
export const LABEL_BG_COLOR = 'rgba(13, 13, 13, 0.75)';

/** GZD square label font */
export const GZD_FONT = 'bold 14px monospace';

/** GZD square label color */
export const GZD_COLOR = 'rgba(200, 164, 21, 0.5)';

/** Label padding (horizontal, vertical) inside background rectangle */
const LABEL_PAD_X = 3;
const LABEL_PAD_Y = 2;

// ─── MgrsGridLayer — custom Leaflet layer ─────────────────────────────────

/**
 * A Leaflet layer that manages a single <canvas> element covering the map
 * container and redraws MGRS grid lines / labels whenever the viewport
 * changes.
 */
const MgrsGridLayer = L.Layer.extend({
  options: {
    visible: true,
  },

  initialize(opts) {
    L.Util.setOptions(this, opts);
    this._canvas = null;
    this._ctx = null;
  },

  onAdd(map) {
    this._map = map;

    // Create the canvas element, sized to the map container
    this._canvas = L.DomUtil.create('canvas', 'mgrs-grid-canvas');
    const size = map.getSize();
    this._canvas.width = size.x;
    this._canvas.height = size.y;
    this._canvas.style.position = 'absolute';
    this._canvas.style.top = '0';
    this._canvas.style.left = '0';
    this._canvas.style.pointerEvents = 'none';
    this._canvas.style.zIndex = '450'; // above tiles, below controls

    map.getPane('overlayPane').appendChild(this._canvas);
    this._ctx = this._canvas.getContext('2d');

    // Align canvas origin with container origin inside the (possibly
    // already-translated) overlay pane so latLngToContainerPoint coords
    // map directly to canvas pixels.
    const topLeft = map.containerPointToLayerPoint([0, 0]);
    L.DomUtil.setPosition(this._canvas, topLeft);

    // Bindable redraw handler
    this._onViewChange = this._onViewChange.bind(this);
    map.on('moveend zoomend resize', this._onViewChange);

    this._draw();
    return this;
  },

  onRemove(map) {
    map.off('moveend zoomend resize', this._onViewChange);

    if (this._canvas && this._canvas.parentNode) {
      this._canvas.parentNode.removeChild(this._canvas);
    }
    this._canvas = null;
    this._ctx = null;
    this._map = null;
    return this;
  },

  /** Respond to viewport changes */
  _onViewChange() {
    if (!this._map || !this._canvas) return;

    // Resize canvas if the container size changed
    const size = this._map.getSize();
    if (this._canvas.width !== size.x || this._canvas.height !== size.y) {
      this._canvas.width = size.x;
      this._canvas.height = size.y;
    }

    // Reposition the canvas to account for pixel-level panning offset.
    // After a pan, Leaflet translates its map panes. We need to reverse
    // that translation so the canvas stays aligned with the container.
    const topLeft = this._map.containerPointToLayerPoint([0, 0]);
    L.DomUtil.setPosition(this._canvas, topLeft);

    this._draw();
  },

  /** Full redraw cycle */
  _draw() {
    if (!this._ctx || !this._map) return;
    const ctx = this._ctx;
    const map = this._map;

    // Clear
    ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    if (!this.options.visible) return;

    const zoom = map.getZoom();
    const b = map.getBounds();
    const bounds = {
      north: b.getNorth(),
      south: b.getSouth(),
      east: b.getEast(),
      west: b.getWest(),
    };

    const interval = getGridInterval(zoom);
    const lines = computeGridLines(bounds, zoom);

    // -- Draw grid lines --
    this._drawGridLines(ctx, map, lines, interval);

    // -- Draw edge labels --
    this._drawEdgeLabels(ctx, map, lines);

    // -- Draw GZD + 100km square ID labels at low zoom --
    if (zoom >= 6 && zoom <= 10) {
      this._drawGzdLabels(ctx, map, bounds, zoom);
    }
  },

  /**
   * Draw all grid lines onto the canvas.
   *
   * Lines at 100km boundaries get a slightly thicker stroke regardless
   * of the current grid interval.
   */
  _drawGridLines(ctx, map, lines, interval) {
    ctx.strokeStyle = GRID_LINE_COLOR;

    // At 100km-only zoom levels all lines are major boundaries
    if (interval >= 100000) {
      ctx.lineWidth = GRID_LINE_WIDTH_MAJOR;
      ctx.beginPath();
      for (const line of lines) {
        const p1 = map.latLngToContainerPoint(line.start);
        const p2 = map.latLngToContainerPoint(line.end);
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
      }
      ctx.stroke();
      return;
    }

    // Sub-100km intervals: separate minor lines from 100km boundaries
    ctx.lineWidth = GRID_LINE_WIDTH;
    ctx.beginPath();

    const majorLines = [];

    for (const line of lines) {
      const p1 = map.latLngToContainerPoint(line.start);
      const p2 = map.latLngToContainerPoint(line.end);

      if (this._isMajorLine(line, interval)) {
        majorLines.push({ p1, p2 });
      } else {
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
      }
    }
    ctx.stroke();

    // Draw major lines with thicker stroke on top
    if (majorLines.length > 0) {
      ctx.lineWidth = GRID_LINE_WIDTH_MAJOR;
      ctx.beginPath();
      for (const { p1, p2 } of majorLines) {
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
      }
      ctx.stroke();
    }
  },

  /**
   * Check whether a grid line sits on a 100km boundary.
   *
   * A major line's label at sub-100km intervals represents the last
   * digits of the easting or northing.  At 10km (2-digit label), "00"
   * marks a 100km boundary; at 1km (also 2-digit), "00" does the same.
   * At 100m (3-digit), "000" marks it.
   */
  _isMajorLine(line, interval) {
    const label = line.label;
    if (interval === 10000) return label === '00';
    if (interval === 1000) return label === '00';
    if (interval === 100) return label === '000';
    return false;
  },

  /**
   * Draw grid labels along the map edges.
   *
   * - Easting (vertical) line labels at top and bottom edges.
   * - Northing (horizontal) line labels at left and right edges.
   */
  _drawEdgeLabels(ctx, map, lines) {
    ctx.font = LABEL_FONT;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';

    const w = this._canvas.width;
    const h = this._canvas.height;
    const edgeMargin = 4; // px from edge

    for (const line of lines) {
      const text = line.label;
      if (!text) continue;

      const metrics = ctx.measureText(text);
      const tw = metrics.width;
      const th = 11; // approximate font height

      if (line.orientation === 'easting') {
        // Vertical line — label at top and bottom of map
        const px = map.latLngToContainerPoint(line.start).x;

        // Only draw if the label is within the canvas bounds
        if (px < 0 || px > w) continue;

        // Top label
        this._drawLabelBg(ctx, px - tw / 2 - LABEL_PAD_X, edgeMargin - LABEL_PAD_Y, tw + LABEL_PAD_X * 2, th + LABEL_PAD_Y * 2);
        ctx.fillStyle = LABEL_COLOR;
        ctx.fillText(text, px, edgeMargin);

        // Bottom label
        const by = h - edgeMargin - th;
        this._drawLabelBg(ctx, px - tw / 2 - LABEL_PAD_X, by - LABEL_PAD_Y, tw + LABEL_PAD_X * 2, th + LABEL_PAD_Y * 2);
        ctx.fillStyle = LABEL_COLOR;
        ctx.fillText(text, px, by);
      } else {
        // Horizontal line — label at left and right edges
        const py = map.latLngToContainerPoint(line.start).y;

        if (py < 0 || py > h) continue;

        ctx.textAlign = 'left';

        // Left label
        this._drawLabelBg(ctx, edgeMargin - LABEL_PAD_X, py - th / 2 - LABEL_PAD_Y, tw + LABEL_PAD_X * 2, th + LABEL_PAD_Y * 2);
        ctx.fillStyle = LABEL_COLOR;
        ctx.textBaseline = 'middle';
        ctx.fillText(text, edgeMargin, py);

        // Right label
        ctx.textAlign = 'right';
        const rx = w - edgeMargin;
        this._drawLabelBg(ctx, rx - tw - LABEL_PAD_X, py - th / 2 - LABEL_PAD_Y, tw + LABEL_PAD_X * 2, th + LABEL_PAD_Y * 2);
        ctx.fillStyle = LABEL_COLOR;
        ctx.fillText(text, rx, py);

        // Reset alignment for next iteration
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
      }
    }
  },

  /**
   * Draw a dark rounded rectangle behind a label for readability.
   */
  _drawLabelBg(ctx, x, y, w, h) {
    ctx.fillStyle = LABEL_BG_COLOR;
    ctx.fillRect(x, y, w, h);
  },

  /**
   * Draw GZD + 100km square ID labels centered in each visible 100km square.
   *
   * Strategy: sample a coarse grid of lat/lng points across the viewport,
   * compute the MGRS at precision 0 (GZD + 100km square), deduplicate,
   * then position the label at the center of each unique square.
   */
  _drawGzdLabels(ctx, map, bounds, zoom) {
    ctx.font = GZD_FONT;
    ctx.fillStyle = GZD_COLOR;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Sample step — coarser at lower zoom
    const stepDeg = zoom <= 7 ? 2 : 1;

    const seen = new Set();
    const labels = [];

    // Clamp to MGRS latitude limits
    const south = Math.max(bounds.south, -80);
    const north = Math.min(bounds.north, 84);

    for (let lat = south; lat <= north; lat += stepDeg) {
      for (let lng = bounds.west; lng <= bounds.east; lng += stepDeg) {
        try {
          const mgrs = latLngToMgrs(lat, lng, 0);
          // precision 0 returns GZD + square ID, e.g. "15TWK"
          // We want to display as "15T WK"
          if (seen.has(mgrs)) continue;
          seen.add(mgrs);

          // Parse GZD and square ID
          const match = mgrs.match(/^(\d{1,2}[A-Z])([A-Z]{2})/);
          if (!match) continue;

          const displayText = `${match[1]} ${match[2]}`;

          // Find approximate center: sample nearby offsets to get the
          // center of the 100km square within the viewport
          labels.push({ text: displayText, lat, lng });
        } catch {
          // skip points outside MGRS range
        }
      }
    }

    for (const label of labels) {
      const pt = map.latLngToContainerPoint(L.latLng(label.lat, label.lng));
      // Only draw if within canvas
      if (pt.x < 0 || pt.x > this._canvas.width || pt.y < 0 || pt.y > this._canvas.height) continue;
      ctx.fillStyle = GZD_COLOR;
      ctx.fillText(label.text, pt.x, pt.y);
    }
  },
});

// ─── Factory function ─────────────────────────────────────────────────────

/**
 * Create and return a new MGRS grid overlay layer.
 *
 * @param {{ visible?: boolean }} [options]
 * @returns {L.Layer}
 */
export function createMgrsGridLayer(options = {}) {
  const opts = { visible: true, ...options };
  return new MgrsGridLayer(opts);
}

// ─── React wrapper ────────────────────────────────────────────────────────

/**
 * React component that manages an MgrsGridLayer on a Leaflet map.
 *
 * Use inside a react-leaflet <MapContainer>. Falls back to the `map`
 * prop if provided, otherwise calls useMap().
 *
 * @param {{ map?: L.Map, visible?: boolean }} props
 */
export function MgrsGridOverlay({ map: mapProp, visible = true }) {
  const contextMap = useMap();
  const map = mapProp || contextMap;
  const layerRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // Create the layer
    const layer = createMgrsGridLayer({ visible });
    layerRef.current = layer;
    map.addLayer(layer);

    return () => {
      if (layerRef.current && map.hasLayer(layerRef.current)) {
        map.removeLayer(layerRef.current);
      }
      layerRef.current = null;
    };
    // Only re-mount when the map instance changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Toggle visibility without remounting the layer
  useEffect(() => {
    if (!layerRef.current) return;

    layerRef.current.options.visible = visible;

    // Trigger a redraw
    if (layerRef.current._draw) {
      layerRef.current._draw();
    }
  }, [visible]);

  return null;
}
